// Sandbox preview mode. A publisher clicks "Preview in sandbox" on the
// dashboard, which opens their own site with a single-use handoff token in the
// URL fragment (#ezsm_sandbox=<handoff>). The widget exchanges it against the
// sandbox instance for a 12h sandbox session, re-points every API call at the
// sandbox host, and renders a banner until the publisher exits or the session
// lapses. See docs/plans/2026-08-05-publisher-sandbox-test-mode.md (Phase 3).

import { establishSandboxSession, getSandboxApiBase, setApiBase, resetApiBase, generateIdempotencyKey } from '@/api/client';
import { allowAds } from '@/adRemoval';
import { clearSession, clearPendingCheckout, isSandboxMode, setSandboxMode, clearSandboxMode, getSandboxGateToken, clearSandboxGateToken } from '@/session';
import { whenBodyReady } from '@/bodyReady';
import { decodeTokenSegment } from '@/tokenPayload';
import { logError } from '@/sdkConsole';

// Mirrors sandboxauth.FragmentKey.
const SANDBOX_FRAGMENT_KEY = 'ezsm_sandbox';

// The nonce is persisted so a re-fired exchange (widget re-init / strict-mode
// / page restore) replays with the same client nonce and gets the original
// session back instead of a token-reused rejection.
const NONCE_KEY = 'ezsm_sandbox_nonce';

const BANNER_HOST_ID = 'ezoic-sm-sandbox';

function getOrCreateNonce(): string {
  try {
    const existing = localStorage.getItem(NONCE_KEY);
    if (existing) return existing;
    const nonce = generateIdempotencyKey();
    localStorage.setItem(NONCE_KEY, nonce);
    return nonce;
  } catch {
    // Storage unavailable: no replay idempotency, the exchange still works once.
    return generateIdempotencyKey();
  }
}

function clearNonce(): void {
  try {
    localStorage.removeItem(NONCE_KEY);
  } catch {
    // ignore
  }
}

// activateSandbox runs first in restoreSession, before any other API call: the
// handoff exchange (or a resumed sandbox session) re-points the API base at
// the sandbox host, and every later fetch must already see it.
export async function activateSandbox(): Promise<void> {
  const handoff = readHandoffFragment();
  if (handoff) {
    await exchangeHandoff(handoff);
    stripHandoffFragment();
    return;
  }
  // Return visit with no fragment: keep pointing at the sandbox host while the
  // preview token is alive (the only credential the gate accepts). Its lapse
  // ends the preview, including any sandbox purchase's visitor session, which
  // is sandbox-signed and dead on live.
  if (!isSandboxMode()) return;
  if (!getSandboxGateToken()) {
    clearSession();
    clearSandboxMode();
    // Drop a preview-minted ad-removal cookie too: the session that justified
    // it is gone, and the shared signing key means Sol would keep suppressing
    // ads on live traffic until the token's exp.
    allowAds();
    return;
  }
  setApiBase(getSandboxApiBase());
  renderBanner();
}

function readHandoffFragment(): string | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  return new URLSearchParams(hash).get(SANDBOX_FRAGMENT_KEY);
}

// stripHandoffFragment removes the handoff from the URL so it does not linger
// in history or get re-processed (same treatment as #ezsm_verify=).
function stripHandoffFragment(): void {
  const hash = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(hash);
  params.delete(SANDBOX_FRAGMENT_KEY);
  const remaining = params.toString();
  const url = window.location.pathname + window.location.search + (remaining ? `#${remaining}` : '');
  window.history.replaceState(null, '', url);
}

async function exchangeHandoff(handoff: string): Promise<void> {
  try {
    await establishSandboxSession(handoff, getOrCreateNonce());
  } catch (err) {
    // Invalid/expired/reused handoff: stay on the live host.
    logError('sandbox handoff exchange failed', err);
    return;
  }
  // Every preview starts as a fresh anonymous visitor: drop any visitor
  // session left over from a previous preview's checkout (or a live login,
  // which 401s against the sandbox host anyway), any pending checkout an
  // interrupted previous preview left behind (resumePendingCheckout would
  // otherwise complete it and re-issue the old subscriber session), and any
  // ad-removal cookie a previous preview (or a live entitlement) minted. Without this the paywall
  // resumes the old subscriber state and the publisher can't re-run the
  // checkout flow. The gate token the exchange just stored lives under its
  // own key and must survive.
  clearSession();
  clearPendingCheckout();
  allowAds();
  setApiBase(getSandboxApiBase());
  setSandboxMode();
  renderBanner();
}

// magicTokenIsSandbox reports whether a magic-link token was minted by the
// sandbox instance (its payload carries sb:true). The widget reads the hint
// from the (unverified) payload segment to route the exchange; the backend
// verifies the signature on exchange.
function magicTokenIsSandbox(token: string): boolean {
  // A magic token is payload.signature (not a JWT): the payload is segment 0.
  return decodeTokenSegment(token.split('.')[0])?.sb === true;
}

// beginSandboxMagicExchange routes the exchange of a sandbox-minted magic
// token at the sandbox host — the cross-browser case, where a content-unlock
// email from a sandbox purchase is opened without an active sandbox session.
// Returns false for live-minted tokens (no routing change).
export function beginSandboxMagicExchange(token: string): boolean {
  if (!magicTokenIsSandbox(token)) return false;
  setApiBase(getSandboxApiBase());
  return true;
}

// finishSandboxMagicExchange adopts sandbox mode after a sandbox magic-token
// exchange that left a preview token (from this exchange or an open preview),
// or reverts to the live host. A sandbox link requested outside a preview
// yields only a sandbox-signed visitor session the gate refuses and live
// rejects, so it is dropped.
export function finishSandboxMagicExchange(sandbox: boolean, ok: boolean): void {
  if (!sandbox) return;
  if (!ok) {
    resetApiBase();
    return;
  }
  if (!getSandboxGateToken()) {
    logError('sandbox login link carried no preview; staying on live');
    clearSession();
    resetApiBase();
    return;
  }
  setSandboxMode();
  renderBanner();
}

// exitSandbox clears the sandbox credentials and reloads; the widget comes
// back up on the live host. allowAds drops any ad-removal cookie minted during
// the preview: the token is signed with the shared environment-agnostic key,
// so left in place Sol would keep suppressing ads on live traffic until exp.
export function exitSandbox(): void {
  clearSession();
  clearSandboxGateToken();
  clearSandboxMode();
  clearNonce();
  allowAds();
  window.location.reload();
}

// renderBanner mounts the fixed "Test mode" pill in its own shadow root so
// publisher CSS can't restyle it and its clicks don't bubble into the
// publisher's document listeners (same isolation pattern as the paywall).
// Amber palette and "test" terminology match the test-mode badges in the
// pubdash SM UI, the subscriber portal, and the checkout card's SandboxBanner.
// Always a bottom-left corner pill: no top bar, no html padding, no collision
// with publisher fixed/sticky navs. See
// docs/plans/2026-09-01-sandbox-test-mode-sticky-nav-fallback.md.
function renderBanner(): void {
  // The widget can run before the parser reaches <body>; defer the mount
  // instead of dropping it.
  if (!document.body) {
    void whenBodyReady().then(renderBanner);
    return;
  }
  if (document.getElementById(BANNER_HOST_ID)) return;
  const host = document.createElement('div');
  host.id = BANNER_HOST_ID;
  host.addEventListener('click', (event: MouseEvent) => event.stopPropagation());
  const root = host.attachShadow({ mode: 'open' });

  // z-index sits below the checkout overlay (2147483000): an open checkout
  // modal covers the pill, and the in-card test-mode pill carries the signal
  // while it's open. Also keeps the pill clear of Stripe's 3DS challenge modal,
  // which renders at the 32-bit ceiling (2147483647).
  const pill = document.createElement('div');
  pill.style.cssText =
    'position:fixed;bottom:24px;left:24px;right:auto;width:auto;z-index:2147482000;display:flex;align-items:center;gap:8px;' +
    'padding:8px 10px 8px 14px;background:#fff8e6;border:1px solid #f0c36d;border-radius:999px;color:#874d00;' +
    'font:14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;' +
    'box-shadow:0 4px 16px rgba(0,0,0,0.12),0 0 0 1px rgba(0,0,0,0.04);';

  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('width', '18');
  icon.setAttribute('height', '18');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('fill', '#d46b08');
  icon.setAttribute('aria-hidden', 'true');
  const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  iconPath.setAttribute('d', 'M7 2v2h1v14a4 4 0 0 0 4 4 4 4 0 0 0 4-4V4h1V2H7zm3 14c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm2-4c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-5h-4V4h4v3z');
  icon.appendChild(iconPath);

  const label = document.createElement('span');
  label.textContent = 'Test mode — no real charges';
  label.style.cssText = 'font-weight:500;letter-spacing:-0.01em;';

  const exit = document.createElement('button');
  exit.type = 'button';
  exit.textContent = 'Exit';
  exit.style.cssText =
    'margin-left:2px;padding:6px 14px;border:1px solid #d46b08;border-radius:999px;background:transparent;color:#d46b08;' +
    'font:inherit;font-weight:600;cursor:pointer;' +
    'transition:background 0.15s ease,color 0.15s ease,border-color 0.15s ease;';
  exit.addEventListener('mouseenter', () => {
    exit.style.background = '#d46b08';
    exit.style.color = '#fff';
    exit.style.borderColor = '#d46b08';
  });
  exit.addEventListener('mouseleave', () => {
    exit.style.background = 'transparent';
    exit.style.color = '#d46b08';
    exit.style.borderColor = '#d46b08';
  });
  exit.addEventListener('click', exitSandbox);

  pill.append(icon, label, exit);
  root.appendChild(pill);
  document.body.appendChild(host);
}
